import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { makeStyles } from "@material-ui/core/styles"
import { get, map } from "lodash"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import HomeIcon from "@material-ui/icons/Home"
import BarChartIcon from "@material-ui/icons/BarChart"
import MoreHorizIcon from "@material-ui/icons/MoreHoriz"
import WhiteLogo from "../assets/images/logo/white_logo.png"
import { getLoggedInUserDisplayName } from "../v2_actions/auth"
import { getInitials } from "../v2_actions/photographer"
import { mobileAllSalesList } from "../v2_actions/all_sales"
import { CurrencyValue } from "./CurrencyValue"

const PAGE_SIZE = 10

export const MobileSales = () => {
    const [sales, setSales] = useState([])
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [loading, setLoading] = useState(false)
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const displayName = getLoggedInUserDisplayName()
    const initials = getInitials(displayName)

    const fetchPage = (pageNum) => {
        setLoading(true)
        dispatch(mobileAllSalesList.updateListFilter({ page: pageNum, items_per_page: PAGE_SIZE }))
        dispatch(mobileAllSalesList.fetchListIfNeeded(true)).then((results) => {
            if (results && results.length > 0) {
                setSales((prev) => pageNum === 1 ? results : [...prev, ...results])
                setHasMore(results.length === PAGE_SIZE)
            } else {
                setHasMore(false)
            }
            setLoading(false)
        })
    }

    useEffect(() => {
        fetchPage(1)
    }, [])

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <img src={WhiteLogo} alt="bossfotos" className={classes.logo} />
                <div className={classes.avatar}>{initials}</div>
            </div>

            <div className={classes.pageContent}>
                <div className={classes.tableTitle}>Customer Sales</div>
                <div className={classes.tableContainer}>
                    <table className={classes.table}>
                        <thead>
                            <tr>
                                <th className={classes.th}>Customer</th>
                                <th className={classes.th}>Gallery</th>
                                <th className={`${classes.th} ${classes.thRight}`}>Photos</th>
                                <th className={`${classes.th} ${classes.thRight}`}>Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sales.length === 0 ? (
                                <tr>
                                    <td className={classes.td} colSpan={4}>
                                        <div className={classes.emptyRow}>No sales data</div>
                                    </td>
                                </tr>
                            ) : (
                                map(sales, (sale, index) => (
                                    <tr key={index}>
                                        <td className={classes.td}>{get(sale, "customer_name")}</td>
                                        <td className={classes.td}>{get(sale, "session_name")}</td>
                                        <td className={`${classes.td} ${classes.tdRight}`}>{get(sale, "num_photos_sold")}</td>
                                        <td className={`${classes.td} ${classes.tdRight}`}>
                                            <CurrencyValue
                                                showCents={true}
                                                value={get(sale, "photographer_amount")}
                                                country={get(sale, "photographer_country")}
                                            />
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                {hasMore && !loading && (
                    <div
                        className={classes.loadMore}
                        onClick={() => {
                            const nextPage = page + 1
                            setPage(nextPage)
                            fetchPage(nextPage)
                        }}
                    >
                        Load More
                    </div>
                )}
            </div>

            <div className={classes.bottomNav}>
                <div className={classes.navItem} onClick={() => history.push("/photographer/map")}>
                    <HomeIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Home</span>
                </div>
                <div className={classes.navItem}>
                    <BarChartIcon className={classes.navIconActive} />
                    <span className={classes.navLabelActive}>Sales</span>
                </div>
                <div className={classes.navItem} onClick={() => history.push("/photographer/mobile-upload")}>
                    <CloudUploadIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>Upload</span>
                </div>
                <div className={classes.navItem}>
                    <MoreHorizIcon className={classes.navIcon} />
                    <span className={classes.navLabel}>More</span>
                </div>
            </div>
        </div>
    )
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        fontFamily: "Helvetica, Raleway, sans-serif",
        paddingBottom: "60px",
    },
    header: {
        background: "linear-gradient(135deg, #0d2137 0%, #1e88e5 100%)",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: "36px",
        width: "auto",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: "#1565c0",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        fontWeight: 600,
        border: "2px solid #fff",
    },
    pageContent: {
        padding: "16px",
        flex: 1,
    },
    tableTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#42a5f5",
        textTransform: "uppercase",
        fontFamily: "Raleway, sans-serif",
        letterSpacing: "0.5px",
        marginBottom: "12px",
    },
    tableContainer: {
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        overflow: "hidden",
    },
    table: {
        width: "100%",
        borderCollapse: "collapse",
    },
    th: {
        backgroundColor: "#1565c0",
        color: "#fff",
        fontSize: "12px",
        fontWeight: 600,
        padding: "10px 12px",
        textAlign: "left",
        whiteSpace: "nowrap",
    },
    thRight: {
        textAlign: "right",
    },
    td: {
        fontSize: "13px",
        fontWeight: 500,
        color: "#333",
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
    },
    tdRight: {
        textAlign: "right",
    },
    emptyRow: {
        textAlign: "center",
        color: "#999",
        padding: "20px 0",
        fontSize: "13px",
    },
    loadMore: {
        textAlign: "center",
        padding: "14px",
        marginTop: "8px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        color: "#1e88e5",
        fontSize: "14px",
        fontWeight: 600,
        cursor: "pointer",
    },
    bottomNav: {
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        padding: "8px 0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    navItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px 12px",
    },
    navIcon: {
        fontSize: "24px",
        color: "#0d2137",
        marginBottom: "2px",
    },
    navIconActive: {
        fontSize: "24px",
        color: "#1e88e5",
        marginBottom: "2px",
    },
    navLabel: {
        fontSize: "10px",
        color: "#0d2137",
    },
    navLabelActive: {
        fontSize: "10px",
        color: "#1e88e5",
        fontWeight: 600,
    },
})
